import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "motion/react";
import Tooltip from "@mui/material/Tooltip";
import useRipple from "../../../hooks/useRipple.js";
import "../../../hooks/useRipple.css";
import UploadPopUp from "../UploadModal/UploadPopUp";
import UploadResponsesModal from "../UploadModal/UploadResponsesModal";
import "./UploadButton.css";
import { getAccountType } from "../../../store/auth/selectors";
import { checkPermission, PERMISSION_TYPES } from "../../utils/permissions";

const UploadButton = ({
  schoolId,
  selectedEvent,
  uploadType,
  selectedTab,
  onUploadSuccess,
  schoolDetails,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  useRipple(buttonRef);
  const accountType = useSelector(getAccountType);

  const isWithinNext60Days = () => {
    if (!selectedEvent?.start_date_time) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const eventDate = new Date(selectedEvent.start_date_time);
    eventDate.setHours(0, 0, 0, 0);

    const sixtyDaysFromEvent = new Date(eventDate);
    sixtyDaysFromEvent.setDate(eventDate.getDate() + 60);

    return today <= sixtyDaysFromEvent;
  };

  const handleUpload = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCloseResponsesModal = () => {
    setShowResponsesModal(false);
  };

  const handleUploadStudents = () => {
    if (isEnabled) {
      setShowModal(true);
    }
  };

  const handleUploadResponses = () => {
    setShowResponsesModal(true);
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const isEnabled = selectedTab === "Events" ? isWithinNext60Days() : true;
  const canUploadOMR = checkPermission(
    accountType,
    PERMISSION_TYPES.UPLOAD_OMR_UPLOADS
  );

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  if (
    !schoolId &&
    uploadType !== "hierarchyUpload" &&
    uploadType !== "uploadOfflineResponses" &&
    uploadType !== "globalStudentsRegister"
  )
    return null;

  const isOfflineEvent =
    selectedTab === "Events" &&
    selectedEvent?.event_type === "OFFLINE" &&
    uploadType === "studentRegister";

  // For Offline events, show dropdown directly instead of button
  if (isOfflineEvent) {
    if (!canUploadOMR) {
      return (
        <div className="upload-button-container" ref={containerRef}>
          <Tooltip
            title={
              !isEnabled
                ? "Upload is allowed only within 60 days from the event's start date"
                : ""
            }
            disableHoverListener={isEnabled}
            disableFocusListener={isEnabled}
            disableTouchListener={isEnabled}
          >
            <span>
              <button
                ref={buttonRef}
                className="upload-button"
                onClick={handleUploadStudents}
                disabled={!isEnabled}
              >
                Upload Students
              </button>
            </span>
          </Tooltip>

          <UploadPopUp
            isOpen={showModal}
            handleCloseModal={handleCloseModal}
            schoolId={schoolId}
            selectedEvent={selectedEvent}
            uploadType={uploadType}
            selectedTab={selectedTab}
            onUploadSuccess={onUploadSuccess}
          />
        </div>
      );
    }

    return (
      <div className="upload-button-container" ref={containerRef}>
        <button
          ref={buttonRef}
          className={`upload-button upload-dropdown-trigger ${
            showDropdown ? "dropdown-open" : ""
          }`}
          onClick={handleDropdownToggle}
        >
          Select Upload Type
          <span className="dropdown-arrow">▼</span>
        </button>

        <AnimatePresence>
          {showDropdown && (
            <motion.div
              key="upload-dropdown"
              className="upload-dropdown"
              initial={{
                opacity: 0,
                scale: 0.8,
                y: -10,
                pointerEvents: "none",
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                pointerEvents: "auto",
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
                y: -10,
                pointerEvents: "none",
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                duration: 0.3,
              }}
            >
              <Tooltip
                title={
                  !isEnabled
                    ? "Upload is allowed only within 60 days from the event's start date"
                    : ""
                }
                disableHoverListener={isEnabled}
                disableFocusListener={isEnabled}
                disableTouchListener={isEnabled}
                followCursor
              >
                <span>
                  <motion.button
                    className="upload-dropdown-item"
                    onClick={handleUploadStudents}
                    disabled={!isEnabled}
                    whileTap={{ scale: 0.98 }}
                  >
                    Upload Students
                  </motion.button>
                </span>
              </Tooltip>
              <motion.button
                className="upload-dropdown-item"
                onClick={handleUploadResponses}
                whileTap={{ scale: 0.98 }}
              >
                Upload OMR PDF
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        <UploadPopUp
          isOpen={showModal}
          handleCloseModal={handleCloseModal}
          schoolId={schoolId}
          selectedEvent={selectedEvent}
          uploadType={uploadType}
          selectedTab={selectedTab}
          onUploadSuccess={onUploadSuccess}
        />

        <UploadResponsesModal
          isOpen={showResponsesModal}
          onClose={handleCloseResponsesModal}
          schoolId={schoolId}
          selectedEvent={selectedEvent}
          schoolDetails={schoolDetails}
        />
      </div>
    );
  }

  // For non-Offline events, show regular button
  return (
    <div className="upload-button-container" ref={containerRef}>
      <Tooltip
        title={
          !isEnabled
            ? "Upload is allowed only within 60 days from the event's start date"
            : ""
        }
        disableHoverListener={isEnabled}
        disableFocusListener={isEnabled}
        disableTouchListener={isEnabled}
      >
        <span>
          <button
            ref={buttonRef}
            className="upload-button"
            onClick={handleUpload}
            disabled={!isEnabled}
          >
            {(() => {
              switch (selectedTab) {
                case "Events":
                  return uploadType === "studentRegister" ||
                    uploadType === "globalStudentsRegister"
                    ? "Upload Students"
                    : "Upload Responses";
                case "Onboarded Students":
                  return "Upload Students";
                case "Hierarchy":
                  return "Upload Hierarchy";
                case "Global":
                  return "Upload Offline Responses";
                default:
                  return "Upload Data";
              }
            })()}
          </button>
        </span>
      </Tooltip>

      <UploadPopUp
        isOpen={showModal}
        handleCloseModal={handleCloseModal}
        schoolId={schoolId}
        selectedEvent={selectedEvent}
        uploadType={uploadType}
        selectedTab={selectedTab}
        onUploadSuccess={onUploadSuccess}
      />
    </div>
  );
};

export default UploadButton;
